import {onUnmounted} from 'vue'

/**
 * Generic polling composable: runs the given callback on an interval while the
 * tab is visible. Owns the interval bookkeeping, pause/resume on tab
 * visibility, error swallowing on background ticks and unmount cleanup, so
 * feature composables only provide the callback and cadence.
 *
 * Must be called synchronously inside <script setup> or setup(): cleanup of
 * the interval and the document visibility listener relies on onUnmounted, so
 * a caller outside a component setup context would leak both.
 */
export function usePolling(callback, {interval}) {

  //Guard against a missing or invalid interval — setInterval(tick, undefined)
  //would degenerate into a ~4ms tight loop of swallowed API calls
  if (!Number.isFinite(interval) || interval <= 0) {
    throw new Error('usePolling requires a positive interval (in ms)')
  }

  //Best-effort background tick used by polling and the visibility handler. A
  //transient failure on a background tick must not become an unhandled
  //rejection (or spam Sentry every tick), so swallow errors here — the next
  //tick recovers. Errors on a user-initiated call of the underlying callback
  //are still surfaced to that caller.
  const tick = () => Promise.resolve().then(callback).catch(() => {})

  //Caller intent (shouldPoll) is tracked separately from the interval:
  //the visibility handler pauses/resumes only the interval, so it can never
  //resurrect polling that the caller stopped (e.g. paused during a send) nor
  //start it in a tab that mounted hidden.
  let pollingInterval = null
  let shouldPoll = false

  //Whether the owning component is still mounted; onUnmounted only fires once,
  //so any code path that could (re)start polling must check this flag
  let isActive = true
  const setPollingInterval = () => {
    clearInterval(pollingInterval)
    pollingInterval = setInterval(tick, interval)
  }
  const clearPollingInterval = () => {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
  const start = () => {

    //No-op once unmounted: callers may pause polling across an awaited async
    //operation, so a resume call can land after onUnmounted already ran its
    //one-time cleanup — an interval created here would poll forever with
    //nothing left to clear it
    if (!isActive) {
      return
    }
    shouldPoll = true

    //Don't run the interval while hidden — the visibility handler starts it
    //once the tab becomes visible
    if (!document.hidden) {
      setPollingInterval()
    }
  }
  const stop = () => {
    shouldPoll = false
    clearPollingInterval()
  }

  //Pause polling while the tab is backgrounded: a hidden tab doesn't need live
  //updates, and leaving a short interval (plus its re-renders) running for
  //hours is wasted CPU. Resume with an immediate tick when the tab comes back.
  const handleVisibilityChange = () => {
    if (document.hidden) {
      clearPollingInterval()
    }
    else if (isActive && shouldPoll) {
      tick()
      setPollingInterval()
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)

  //Stop polling and unbind the visibility listener on unmount. The isActive
  //flag must go false first so late resume calls (e.g. a send that resolves
  //after navigation) can't restart the interval.
  onUnmounted(() => {
    isActive = false
    stop()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  //Expose interface
  return {
    start,
    stop,
  }
}
